import Cookies from "js-cookie";
/**
 * TranscriptionService - Handles audio recording and transcription functionality
 */
export default class TranscriptionService {
  constructor() {
    this.socketRef = null;
    this.streamRef = null;
    this.audioContextRef = null;
    this.processorRef = null;
    this.sourceRef = null;
    this.partialTranscriptRef = "";
    this.finalTranscriptRef = "";
  }

  /**
   * Detects if the browser is Firefox
   * @returns {boolean} True if Firefox, false otherwise
   */
  isFirefox() {
    return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
  }

  /**
   * Converts Float32 audio data to Int16 PCM format
   * @param {Float32Array} float32Array - Float32 audio data
   * @returns {Int16Array} Converted Int16 audio data
   */
  convertFloat32ToInt16(float32Array) {
    const int16Array = new Int16Array(float32Array.length);
    for (let i = 0; i < float32Array.length; i++) {
      int16Array[i] = Math.max(
        -32768,
        Math.min(32767, float32Array[i] * 32768)
      );
    }
    return int16Array;
  }

  /**
   * Starts WebSocket transcription
   * @param {Function} setIsConnecting - State setter for connecting status
   * @param {Function} setIsRecording - State setter for recording status
   * @param {Function} setTranscript - State setter for transcript
   * @param {Function} setFinalTranscript - State setter for final transcript
   * @returns {Promise<void>}
   */
  async startWebSocketTranscription(
    setIsConnecting,
    setIsRecording,
    setTranscript,
    setFinalTranscript
  ) {
    try {
      // Get WebSocket URL from environment variable
      let wsUrl = process.env.REACT_APP_WEBSOCKET_TRANSCRIBE_URL;

      if (!wsUrl) {
        return;
      }

      setIsConnecting(true);
      const token = Cookies.get("idtoken");
      wsUrl += "?token=" + token + "&language_code=en-US&sample_rate=16000";
      // Initialize WebSocket
      this.socketRef = new WebSocket(wsUrl);

      this.socketRef.onopen = async () => {
        setIsConnecting(false);

        try {
          // Setup audio processing
          this.streamRef = await navigator.mediaDevices.getUserMedia({
            audio: {
              sampleRate: 16000,
              channelCount: 1,
              echoCancellation: true,
              noiseSuppression: true,
            },
          });

          // Create AudioContext with or without sampleRate based on browser
          if (this.isFirefox()) {
            // Firefox - create AudioContext without sampleRate parameter
            this.audioContextRef = new (window.AudioContext ||
              window.webkitAudioContext)();
          } else {
            // Other browsers - create AudioContext with sampleRate
            this.audioContextRef = new (window.AudioContext ||
              window.webkitAudioContext)({
                sampleRate: 16000,
              });
          }

          this.sourceRef = this.audioContextRef.createMediaStreamSource(
            this.streamRef
          );
          this.processorRef = this.audioContextRef.createScriptProcessor(
            4096,
            1,
            1
          );

          // Process audio data and send to WebSocket
          this.processorRef.onaudioprocess = (event) => {
            if (
              this.socketRef &&
              this.socketRef.readyState === WebSocket.OPEN
            ) {
              const pcmData = event.inputBuffer.getChannelData(0);
              const pcmInt16 = this.convertFloat32ToInt16(pcmData);
              this.socketRef.send(pcmInt16);
            }
          };

          // Connect audio nodes
          this.sourceRef.connect(this.processorRef);
          this.processorRef.connect(this.audioContextRef.destination);

          setIsRecording(true);
        } catch (audioError) {
          console.error("Error setting up audio:", audioError);
          setIsConnecting(false);
          this.stopWebSocketTranscription(setIsRecording, setIsConnecting);

          let errorMessage = "Failed to access microphone. ";
          if (audioError.name === "NotAllowedError") {
            errorMessage += "Please grant microphone permissions.";
          } else if (audioError.name === "NotFoundError") {
            errorMessage += "No microphone found. Please connect a microphone.";
          } else {
            errorMessage += `Error: ${audioError.message}`;
          }
        }
      };

      // Handle incoming transcription messages
      this.socketRef.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const transcriptText = data.transcript || "";
          const isPartial = data.is_partial;

          if (transcriptText.trim()) {
            if (isPartial) {
              // Update partial transcript - combine with existing final transcript
              this.partialTranscriptRef = transcriptText;
              const combinedTranscript =
                this.finalTranscriptRef +
                (this.finalTranscriptRef ? " " : "") +
                transcriptText;
              setTranscript(combinedTranscript);
            } else {
              // Final transcript - append to existing final transcript and update state
              const newFinalTranscript =
                this.finalTranscriptRef +
                (this.finalTranscriptRef ? " " : "") +
                transcriptText;
              this.finalTranscriptRef = newFinalTranscript; // Update ref
              setFinalTranscript(newFinalTranscript);
              setTranscript(newFinalTranscript);
              this.partialTranscriptRef = ""; // Clear partial
            }
          }
        } catch (error) {
          console.error("Error parsing transcription:", error);
          // Fallback: treat as plain text and append to existing transcript
          if (typeof event.data === "string" && event.data.trim()) {
            const newText = event.data.trim();
            const newFinalTranscript =
              this.finalTranscriptRef +
              (this.finalTranscriptRef ? " " : "") +
              newText;
            this.finalTranscriptRef = newFinalTranscript; // Update ref
            setFinalTranscript(newFinalTranscript);
            setTranscript(newFinalTranscript);
          }
        }
      };

      this.socketRef.onerror = (error) => {
        console.error("WebSocket Error:", error);
        console.error("Failed to connect to:", wsUrl);
        setIsConnecting(false);
        this.stopWebSocketTranscription(setIsRecording, setIsConnecting);
      };

      this.socketRef.onclose = (event) => {
        setIsConnecting(false);
        // Don't auto-stop recording on close unless it's unexpected
        if (event.code !== 1000) {
          // 1000 is normal closure
          this.stopWebSocketTranscription(setIsRecording, setIsConnecting);
        }
      };
    } catch (error) {
      console.error("Error starting WebSocket transcription:", error);
      setIsConnecting(false);
      this.stopWebSocketTranscription(setIsRecording, setIsConnecting);
    }
  }

  /**
   * Stops WebSocket transcription
   * @param {Function} setIsRecording - State setter for recording status
   * @param {Function} setIsConnecting - State setter for connecting status
   */
  stopWebSocketTranscription(setIsRecording, setIsConnecting) {
    try {
      // Close WebSocket gracefully
      if (
        this.socketRef &&
        this.socketRef.readyState === WebSocket.OPEN
      ) {
        this.socketRef.close(1000, "Recording stopped by user"); // Normal closure
      }

      // Clean up audio context
      if (this.processorRef && this.sourceRef) {
        this.sourceRef.disconnect(this.processorRef);
        this.processorRef.disconnect();
      }

      if (
        this.audioContextRef &&
        this.audioContextRef.state !== "closed"
      ) {
        this.audioContextRef.close();
      }

      // Stop media stream
      if (this.streamRef) {
        this.streamRef.getTracks().forEach((track) => track.stop());
      }

      setIsRecording(false);
      if (setIsConnecting) {
        setIsConnecting(false);
      }

      // Reset refs but keep finalTranscriptRef for accumulation
      this.socketRef = null;
      this.audioContextRef = null;
      this.processorRef = null;
      this.sourceRef = null;
      this.streamRef = null;
      this.partialTranscriptRef = "";
      // Don't reset finalTranscriptRef here - keep it for accumulation
    } catch (error) {
      console.error("Error stopping recording:", error);
    }
  }

  /**
   * Clears the transcript
   */
  clearTranscript() {
    this.finalTranscriptRef = "";
    this.partialTranscriptRef = "";
  }

  /**
   * Updates the final transcript
   * @param {string} transcript - New transcript text
   */
  updateTranscript(transcript) {
    this.finalTranscriptRef = transcript;
  }

  /**
   * Gets the current final transcript
   * @returns {string} Current final transcript
   */
  getFinalTranscript() {
    return this.finalTranscriptRef;
  }
}
